import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { HiRefresh, HiPencil, HiChevronLeft, HiChevronRight } from "react-icons/hi";
import KYCEditModal from "./KYCEditModal";
import Spinner from "../shared/Spinner";
import { fetchKYCSubmissions, updateKYCStatus } from "../../services/kycService";

// Debounce hook for search optimization
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

function KYCList() {
  const [allSubmissions, setAllSubmissions] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [filteredSubmissions, setFilteredSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 15;
  const navigate = useNavigate();
  
  // Track if modal made changes (to avoid unnecessary refetch)
  const modalSavedRef = useRef(false);
  
  // Debounce search term (300ms delay)
  const debouncedSearchTerm = useDebounce(searchTerm, 300);

  // Get admin info and verify access
  const adminInfo = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("adminInfo") || "{}");
    } catch {
      return {};
    }
  }, []);

  const userRole = adminInfo.role;
  const token = adminInfo.token || (() => {
    const cookieArr = document.cookie.split(";");
    for (let cookie of cookieArr) {
      const [key, value] = cookie.split("=");
      if (key.trim() === "token") {
        return decodeURIComponent(value);
      }
    }
    return null;
  })();

  // Check if user has access (superadmin or admin only)
  const hasAccess = userRole === "superadmin" || userRole === "admin";

  useEffect(() => {
    // Verify token and role on mount
    if (!token) {
      navigate("/login", { replace: true });
      return;
    }

    // Check token expiration
    try {
      const expireTime = JSON.parse(localStorage.getItem("expireTime") || "null");
      if (expireTime && new Date().getTime() > new Date(expireTime).getTime()) {
        navigate("/login", { replace: true });
        return;
      }
    } catch {
      navigate("/login", { replace: true });
      return;
    }

    // Check role access
    if (!hasAccess) {
      navigate("/", { replace: true });
      return;
    }

    // Load data from API
    const loadData = async () => {
      setLoading(true);
      try {
        const data = await fetchKYCSubmissions(1, 1000, null);
        setAllSubmissions(data);
        applyFiltersAndSearch(data, statusFilter, searchTerm);
      } catch (error) {
        console.error("Error loading KYC submissions:", error);
        const errorMessage =
          error.message || "Failed to load KYC submissions. Please try again.";

        if (error.message?.includes("Unauthorized")) {
          alert("Session expired. Please login again.");
          navigate("/login", { replace: true });
          return;
        }

        if (error.message?.includes("Forbidden")) {
          alert("Access denied. You do not have permission to view KYC submissions.");
          navigate("/", { replace: true });
          return;
        }

        alert(errorMessage);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [token, hasAccess, navigate]);

  // Refresh function - forces fresh fetch from server and resets all filters
  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      // Reset filters and search to default
      setStatusFilter("All");
      setSearchTerm("");
      setCurrentPage(1);
      
      // Force fresh fetch from server (ignore any cached data)
      const data = await fetchKYCSubmissions(1, 1000, null);
      setAllSubmissions(data);
      setFilteredSubmissions(data); // Show all data since filters are reset
    } catch (error) {
      console.error("Error refreshing KYC submissions:", error);
      const errorMessage =
        error.message || "Failed to refresh submissions. Please try again.";

      if (error.message?.includes("Unauthorized")) {
        alert("Session expired. Please login again.");
        navigate("/login", { replace: true });
        return;
      }

      alert(errorMessage);
    } finally {
      setRefreshing(false);
    }
  };

  const handleEdit = (submission) => {
    console.log("KYC edit opened for userId:", submission?.userId, "submissionId:", submission?.id);
    setSelectedSubmission(submission);
  };

  const handleCloseModal = () => {
    setSelectedSubmission(null);
    
    // Only refetch if a save was made (skip unnecessary API call)
    if (modalSavedRef.current) {
      modalSavedRef.current = false; // Reset flag
      const reloadData = async () => {
        try {
          const data = await fetchKYCSubmissions(1, 1000, null);
          setAllSubmissions(data);
          // Reapply filters after reload
          applyFiltersAndSearch(data, statusFilter, debouncedSearchTerm);
          setCurrentPage(1);
        } catch (error) {
          console.error("Error reloading KYC submissions:", error);
          // Don't show alert on background reload, just log the error
        }
      };
      reloadData();
    }
  };

  const handleSave = async (id, newStatus, rejectionReason = "", expiryDate = null) => {
    try {
      const submission = allSubmissions.find((sub) => sub.id === id);
      const userId =
        submission?.userId ||
        (typeof id === "string" && id.startsWith("user-")
          ? parseInt(id.replace("user-", ""))
          : parseInt(id));

      if (!userId) {
        throw new Error("Unable to find user ID for this submission");
      }

      console.log("Updating KYC status for userId:", userId, "kycId:", submission?.kycId, "status:", newStatus);
      await updateKYCStatus(
        userId,
        newStatus,
        rejectionReason,
        submission?.kycId || null,
        expiryDate
      );

      // Mark that a save occurred so modal close will refetch
      modalSavedRef.current = true;

      // Update local state optimistically while modal closes
      const updatedSubmissions = allSubmissions.map((sub) =>
        sub.id === id
          ? { ...sub, kycVerificationStatus: newStatus, rejectionReason, expiryDate }
          : sub
      );
      setAllSubmissions(updatedSubmissions);
      applyFiltersAndSearch(updatedSubmissions, statusFilter, debouncedSearchTerm);
    } catch (error) {
      console.error("Error updating KYC status:", error);
      const errorMessage =
        error.message || "Failed to update KYC status. Please try again.";

      if (error.message?.includes("Unauthorized")) {
        alert("Session expired. Please login again.");
        navigate("/login", { replace: true });
        return;
      }

      if (error.message?.includes("Forbidden")) {
        alert("Access denied. You do not have permission to update KYC status.");
        return;
      }

      alert(errorMessage);
      throw error; // Re-throw to prevent modal from closing
    }
  };

  // Apply filters and search - memoized to avoid recreating on every render
  const applyFiltersAndSearch = useCallback((data, filter, search) => {
    let filtered = data;

    // Apply status filter
    if (filter !== "All") {
      filtered = filtered.filter(
        (sub) => sub.kycVerificationStatus === filter
      );
    }

    // Apply search
    if (search.trim()) {
      const searchLower = search.toLowerCase().trim();
      filtered = filtered.filter((sub) => {
        return (
          sub.fullName?.toLowerCase().includes(searchLower) ||
          sub.phoneNumber?.toLowerCase().includes(searchLower) ||
          sub.telegramChatId?.toLowerCase().includes(searchLower) ||
          sub.companyName?.toLowerCase().includes(searchLower)
        );
      });
    }

    setFilteredSubmissions(filtered);
    // Reset to page 1 when filters change
    setCurrentPage(1);
  }, []);

  // Calculate paginated submissions based on current page and filtered data
  const paginatedSubmissions = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filteredSubmissions.slice(startIndex, endIndex);
  }, [currentPage, filteredSubmissions, itemsPerPage]);

  // Update submissions when pagination changes
  useEffect(() => {
    setSubmissions(paginatedSubmissions);
  }, [paginatedSubmissions]);

  // Handle page change
  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    // Scroll to top of page (pagination is now above table)
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Calculate total pages
  const totalPages = Math.ceil(filteredSubmissions.length / itemsPerPage);

  // Handle filter change - use local data instead of refetching
  const handleFilterChange = (e) => {
    const newFilter = e.target.value;
    setStatusFilter(newFilter);
    // Filter locally from already-loaded data (no API call needed)
    applyFiltersAndSearch(allSubmissions, newFilter, debouncedSearchTerm);
  };

  // Handle search change - just update state, debounced effect handles filtering
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  // Apply filters when debounced search term changes
  useEffect(() => {
    if (allSubmissions.length > 0) {
      applyFiltersAndSearch(allSubmissions, statusFilter, debouncedSearchTerm);
    }
  }, [debouncedSearchTerm, allSubmissions, statusFilter]);

  const getStatusBadge = (status) => {
    const baseClasses = "px-3 py-1 rounded-full text-sm font-medium";
    switch (status) {
      case "approved":
        return `${baseClasses} bg-green-100 text-green-800`;
      case "rejected":
        return `${baseClasses} bg-red-100 text-red-800`;
      case "pending":
        return `${baseClasses} bg-yellow-100 text-yellow-800`;
      case "expired":
        return `${baseClasses} bg-purple-100 text-purple-800`;
      default:
        return `${baseClasses} bg-gray-100 text-gray-800`;
    }
  };

  // Show loading while checking access
  if (loading) {
    return <Spinner size="lg" centered={true} />;
  }

  // Additional safety check - should not reach here if RequireRole works correctly
  if (!hasAccess) {
    return (
      <div className="p-4">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
          <h2 className="text-2xl font-bold text-red-800 mb-2">Access Denied</h2>
          <p className="text-red-600">
            You do not have permission to access KYC management. Only superadmin and admin roles are allowed.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-2 sm:p-4">
      <div className="mb-4 sm:mb-6">
        <h1 className="text-xl sm:text-2xl font-bold text-gray-900">KYC Submissions</h1>
        <p className="text-sm sm:text-base text-gray-600 mt-1 sm:mt-2">
          Manage and verify KYC submissions from users
        </p>
      </div>

      {/* Search and Filter Section */}
      <div className="bg-white rounded-lg shadow p-3 sm:p-4 mb-3 sm:mb-4">
        <div className="flex flex-col md:flex-row gap-3 sm:gap-4">
          {/* Search Input */}
          <div className="flex-1">
            <label htmlFor="search" className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">
              Search
            </label>
            <input
              type="text"
              id="search"
              value={searchTerm}
              onChange={handleSearchChange}
              placeholder="Search by name, phone, telegram ID, or company..."
              className="w-full px-3 sm:px-4 py-2 text-sm sm:text-base border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
            />
          </div>

          {/* Filter Dropdown and Refresh Button */}
          <div className="w-full md:w-48">
            <label htmlFor="filter" className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">
              Filter by Status
            </label>
            <div className="flex gap-2">
              <select
                id="filter"
                value={statusFilter}
                onChange={handleFilterChange}
                className="flex-1 px-3 sm:px-4 py-2 text-sm sm:text-base border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none bg-white"
              >
                <option value="All">All</option>
                <option value="pending">pending</option>
                <option value="approved">approved</option>
                <option value="rejected">rejected</option>
                <option value="expired">expired</option>
              </select>
              <button
                onClick={handleRefresh}
                disabled={refreshing}
                className="px-3 sm:px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center min-w-[44px]"
                title="Refresh submissions"
                aria-label="Refresh submissions"
              >
                <HiRefresh
                  className={`text-lg sm:text-xl ${refreshing ? "animate-spin" : ""}`}
                />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Pagination Controls - Always visible */}
      {filteredSubmissions.length > 0 && (
        <div className="bg-white rounded-lg shadow p-4 mt-4">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="text-sm text-gray-700">
              Showing{" "}
              <span className="font-medium">
                {filteredSubmissions.length === 0
                  ? 0
                  : (currentPage - 1) * itemsPerPage + 1}
              </span>{" "}
              to{" "}
              <span className="font-medium">
                {Math.min(currentPage * itemsPerPage, filteredSubmissions.length)}
              </span>{" "}
              of <span className="font-medium">{filteredSubmissions.length}</span>{" "}
              results
            </div>

            {totalPages > 0 && (
              <div className="flex items-center gap-2">
                {/* Previous Button */}
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="p-2 border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  aria-label="Previous page"
                >
                  <HiChevronLeft className="text-lg" />
                </button>

                {/* Page Numbers */}
                <div className="flex items-center gap-1">
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => {
                    // Show first page, last page, current page, and pages around current
                    if (
                      page === 1 ||
                      page === totalPages ||
                      (page >= currentPage - 1 && page <= currentPage + 1) ||
                      totalPages <= 5
                    ) {
                      return (
                        <button
                          key={page}
                          onClick={() => handlePageChange(page)}
                          className={`px-3 py-2 text-sm border rounded-md transition-colors ${
                            currentPage === page
                              ? "bg-blue-600 text-white border-blue-600"
                              : "border-gray-300 hover:bg-gray-50 text-gray-700"
                          }`}
                        >
                          {page}
                        </button>
                      );
                    } else if (
                      page === currentPage - 2 ||
                      page === currentPage + 2
                    ) {
                      return (
                        <span key={page} className="px-2 text-gray-500">
                          ...
                        </span>
                      );
                    }
                    return null;
                  })}
                </div>

                {/* Next Button */}
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="p-2 border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  aria-label="Next page"
                >
                  <HiChevronRight className="text-lg" />
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      <div className="bg-white rounded-lg shadow overflow-hidden">
        {/* Desktop Table View */}
        <div className="hidden md:block overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
                <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Profile
                </th>
                <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Full Name
                </th>
                <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Phone Number
                </th>
                <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Telegram ID
                </th>
                <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Company
                </th>
                <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  KYC Status
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {submissions.length === 0 ? (
                <tr>
                  <td
                    colSpan="7"
                    className="px-4 lg:px-6 py-4 text-center text-gray-500"
                  >
                    No KYC submissions found
                  </td>
                </tr>
              ) : (
                submissions.map((submission) => (
                  <tr key={submission.id} className="hover:bg-gray-50">
                    <td className="px-4 lg:px-6 py-4 whitespace-nowrap">
                      <button
                        onClick={() => handleEdit(submission)}
                        className="p-2 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-md transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1"
                        title="Edit submission"
                        aria-label="Edit submission"
                      >
                        <HiPencil className="text-lg" />
                      </button>
                    </td>
                    <td className="px-4 lg:px-6 py-4 whitespace-nowrap">
                      {submission.userImageUrl ? (
                        <img
                          src={submission.userImageUrl}
                          alt={submission.fullName}
                          className="w-10 h-10 rounded-full object-cover border-2 border-gray-300"
                        />
                      ) : (
                        <div className="w-10 h-10 rounded-full bg-gray-200 border-2 border-gray-300 flex items-center justify-center">
                          <span className="text-xs text-gray-500">N/A</span>
                        </div>
                      )}
                    </td>
                    <td className="px-4 lg:px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {submission.fullName}
                    </td>
                    <td className="px-4 lg:px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {submission.phoneNumber}
                    </td>
                    <td className="px-4 lg:px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {submission.telegramChatId}
                    </td>
                    <td className="px-4 lg:px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {submission.companyName}
                    </td>
                    <td className="px-4 lg:px-6 py-4 whitespace-nowrap">
                      <span className={getStatusBadge(submission.kycVerificationStatus)}>
                        {submission.kycVerificationStatus}
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Mobile Card View */}
        <div className="md:hidden">
          {submissions.length === 0 ? (
            <div className="p-6 text-center text-gray-500">
              No KYC submissions found
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {submissions.map((submission) => (
                <div key={submission.id} className="p-4 hover:bg-gray-50">
                  <div className="flex justify-between items-start mb-3">
                    <div className="flex-1">
                      <h3 className="text-base font-semibold text-gray-900 mb-1">
                        {submission.fullName}
                      </h3>
                      <p className="text-sm text-gray-600">{submission.companyName}</p>
                    </div>
                    <span className={getStatusBadge(submission.kycVerificationStatus)}>
                      {submission.kycVerificationStatus}
                    </span>
                  </div>
                  <div className="space-y-2 mb-3">
                    <div className="flex items-center text-sm">
                      <span className="text-gray-500 w-24">Phone:</span>
                      <span className="text-gray-900">{submission.phoneNumber}</span>
                    </div>
                    <div className="flex items-center text-sm">
                      <span className="text-gray-500 w-24">Telegram:</span>
                      <span className="text-gray-900 break-all">{submission.telegramChatId}</span>
                    </div>
                  </div>
                  <button
                    onClick={() => handleEdit(submission)}
                    className="w-full mt-3 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors text-sm font-medium"
                  >
                    Edit
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {selectedSubmission && (
        <KYCEditModal
          submission={selectedSubmission}
          onClose={handleCloseModal}
          onSave={handleSave}
        />
      )}
    </div>
  );
}

export default KYCList;
